import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Typography, Paper, Table, TableHead, TableRow, TableCell, TableBody, CircularProgress, Alert, MenuItem, Select, FormControl, InputLabel, IconButton, Button, TextField, InputAdornment, Chip, Tooltip, Snackbar, TableSortLabel, TablePagination, Tabs, Tab, Badge } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import SearchIcon from '@mui/icons-material/Search';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FiberNewOutlinedIcon from '@mui/icons-material/FiberNewOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import HighlightOffOutlinedIcon from '@mui/icons-material/HighlightOffOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import { getNewLeadData, getQualifiedLeadData, getOtherLeadData } from '../../Action/api';
import dayjs from 'dayjs';
import LeadDetailsModal from './LeadDetailsModal'; // <-- Import the modal
import AddLeadDialog from './AddLeadDialog';
import BulkImportLeadsDialog from './BulkImportLeadsDialog';
import FollowUpCommentsDialog from './FollowUpCommentsDialog';

const monthOptions = Array.from({ length: 7 }).map((_, i) => {
  const offset = i - 3;
  const date = dayjs().add(offset, 'month');
  return {
    label: date.format('MMMM YYYY'),
    value: date.format('MMM-YYYY'),
  };
});

export const Leads = () => {
  const [leadTab, setLeadTab] = useState('new');
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [month, setMonth] = useState(dayjs().format('MMM-YYYY'));
  const [meta, setMeta] = useState({ count: 0, uiMsg: '' });

  // Client-side filters for a smoother table browsing experience.
  const [searchTerm, setSearchTerm] = useState('');
  const [planFilter, setPlanFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [requestTypeFilter, setRequestTypeFilter] = useState('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  // ADD: Modal state
  const [selectedLead, setSelectedLead] = useState(null);
  const [openAddLead, setOpenAddLead] = useState(false);
  const [openBulkImport, setOpenBulkImport] = useState(false);
  const [commentsLeadId, setCommentsLeadId] = useState(null);
  const [copyMessage, setCopyMessage] = useState('');
  const [sortBy, setSortBy] = useState('createdDate');
  const [sortOrder, setSortOrder] = useState('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const leadTabConfig = useMemo(
    () => ({
      new: { label: 'New Leads', color: '#4B5AB6', bg: '#EEF2FF', icon: <FiberNewOutlinedIcon fontSize="small" /> },
      qualified: { label: 'Qualified', color: '#16A34A', bg: '#ECFDF3', icon: <CheckCircleOutlineIcon fontSize="small" /> },
      disqualified: { label: 'Disqualified', color: '#DC2626', bg: '#FEF2F2', icon: <CancelOutlinedIcon fontSize="small" /> },
      won: { label: 'Won', color: '#CA8A04', bg: '#FFFBEB', icon: <EmojiEventsOutlinedIcon fontSize="small" /> },
      loss: { label: 'Loss', color: '#EF4444', bg: '#FFF1F2', icon: <HighlightOffOutlinedIcon fontSize="small" /> },
    }),
    []
  );

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (leadTab === 'new') {
        const response = await getNewLeadData(month);
        setLeads(response.data || []);
        setMeta({ count: response.count, uiMsg: response.uiMsg });
      } else if (leadTab === 'qualified') {
        const response = await getQualifiedLeadData(month);
        setLeads(response.data || []);
        setMeta({ count: response.count || (response.data || []).length, uiMsg: response.uiMsg });
      } else {
        const response = await getOtherLeadData(month);
        const typeMap = {
          disqualified: 'Disqualified',
          won: 'Closed - Won',
          loss: 'Closed - Loss',
        };
        const filtered = (response.data || []).filter(lead => lead.leadType === typeMap[leadTab]);
        setLeads(filtered);
        setMeta({ count: filtered.length, uiMsg: response.uiMsg });
      }
    } catch (err) {
      setError(`Failed to load ${leadTabConfig[leadTab]?.label?.toLowerCase() || 'leads'}`);
    }
    setLoading(false);
  }, [leadTab, leadTabConfig, month]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  useEffect(() => {
    setSelectedLead(null);
  }, [leadTab]);

  const handleOpenAddLead = () => setOpenAddLead(true);
  const handleCloseAddLead = () => setOpenAddLead(false);

  const handleLeadCreated = () => {
    handleCloseAddLead();
    fetchLeads();
  };

  const handleOpenBulkImport = () => setOpenBulkImport(true);
  const handleCloseBulkImport = () => setOpenBulkImport(false);

  const normalize = value => (value || '').toString().trim().toLowerCase();
  const readable = value => (value || '').toString().trim();

  const planOptions = useMemo(
    () => Array.from(new Set(leads.map(lead => readable(lead.planType)).filter(Boolean))).sort((a, b) => a.localeCompare(b)),
    [leads]
  );

  const sourceOptions = useMemo(
    () => Array.from(new Set(leads.map(lead => readable(lead.source)).filter(Boolean))).sort((a, b) => a.localeCompare(b)),
    [leads]
  );

  const requestTypeOptions = useMemo(
    () => Array.from(new Set(leads.map(lead => readable(lead.requestType)).filter(Boolean))).sort((a, b) => a.localeCompare(b)),
    [leads]
  );

  const filteredLeads = useMemo(() => {
    const term = normalize(searchTerm);

    return leads.filter(lead => {
      const matchesSearch =
        !term ||
        [
          lead.fullName,
          lead.email,
          lead.phoneNumber,
          lead.country,
          lead.planType,
          lead.source,
          lead.requestType,
          lead.reasonForRequest,
        ]
          .map(normalize)
          .some(value => value.includes(term));

      const matchesPlan = planFilter === 'all' || normalize(lead.planType) === normalize(planFilter);
      const matchesSource = sourceFilter === 'all' || normalize(lead.source) === normalize(sourceFilter);
      const matchesRequestType = requestTypeFilter === 'all' || normalize(lead.requestType) === normalize(requestTypeFilter);

      // Date range filter
      let matchesDateRange = true;
      if (fromDate || toDate) {
        const leadDate = lead.createdDate ? new Date(lead.createdDate) : null;
        if (leadDate) {
          if (fromDate) {
            const from = new Date(fromDate);
            from.setHours(0, 0, 0, 0);
            if (leadDate < from) matchesDateRange = false;
          }
          if (toDate) {
            const to = new Date(toDate);
            to.setHours(23, 59, 59, 999);
            if (leadDate > to) matchesDateRange = false;
          }
        } else {
          matchesDateRange = false;
        }
      }

      return matchesSearch && matchesPlan && matchesSource && matchesRequestType && matchesDateRange;
    });
  }, [leads, searchTerm, planFilter, sourceFilter, requestTypeFilter, fromDate, toDate]);

  const sortedLeads = useMemo(() => {
    const safeDate = value => {
      const time = new Date(value).getTime();
      return Number.isNaN(time) ? 0 : time;
    };

    const sorted = [...filteredLeads].sort((a, b) => {
      if (sortBy === 'createdDate') {
        return safeDate(a.createdDate) - safeDate(b.createdDate);
      }

      if (sortBy === 'fullName') {
        return (a.fullName || '').localeCompare(b.fullName || '');
      }

      return 0;
    });

    return sortOrder === 'asc' ? sorted : sorted.reverse();
  }, [filteredLeads, sortBy, sortOrder]);

  const paginatedLeads = useMemo(
    () => sortedLeads.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sortedLeads, page, rowsPerPage]
  );

  const activeFilterCount = [
    searchTerm.trim() !== '',
    planFilter !== 'all',
    sourceFilter !== 'all',
    requestTypeFilter !== 'all',
    fromDate !== '',
    toDate !== '',
  ].filter(Boolean).length;

  const handleResetFilters = () => {
    setSearchTerm('');
    setPlanFilter('all');
    setSourceFilter('all');
    setRequestTypeFilter('all');
    setFromDate('');
    setToDate('');
  };

  useEffect(() => {
    setPage(0);
  }, [searchTerm, planFilter, sourceFilter, requestTypeFilter, fromDate, toDate, month, rowsPerPage]);

  const handleSort = column => {
    if (sortBy === column) {
      setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setSortBy(column);
    setSortOrder(column === 'createdDate' ? 'desc' : 'asc');
  };

  const handleCopy = async (event, value, label) => {
    event.stopPropagation();

    if (!value) {
      setCopyMessage(`No ${label} to copy`);
      return;
    }

    try {
      await navigator.clipboard.writeText(value);
      setCopyMessage(`${label} copied`);
    } catch (copyError) {
      setCopyMessage(`Unable to copy ${label}`);
    }
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Paper elevation={3} sx={{ p: { xs: 1.5, sm: 2, md: 3 }, mb: 3, borderRadius: 3 }}>
        {/* Title */}
        <Typography variant="h5" sx={{ fontWeight: 700, color: leadTabConfig[leadTab].color, mb: 2 }}>
          {leadTabConfig[leadTab].label}
        </Typography>

        <Paper
          elevation={0}
          sx={{
            mb: 2,
            p: 1,
            borderRadius: 3,
            border: '1px solid #e2e8f0',
            bgcolor: '#f8fafc',
          }}
        >
          <Tabs
            value={leadTab}
            onChange={(_, value) => setLeadTab(value)}
            variant="scrollable"
            scrollButtons="auto"
            TabIndicatorProps={{ style: { display: 'none' } }}
            sx={{
              minHeight: 'auto',
              '& .MuiTabs-flexContainer': {
                gap: 1,
              },
            }}
          >
            {Object.entries(leadTabConfig).map(([key, config]) => (
              <Tab
                key={key}
                value={key}
                icon={config.icon}
                iconPosition="start"
                label={config.label}
                sx={{
                  minHeight: 40,
                  textTransform: 'none',
                  borderRadius: 2,
                  px: 1.75,
                  py: 1,
                  fontSize: { xs: '0.78rem', sm: '0.85rem' },
                  fontWeight: 700,
                  color: '#475569',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: '#eef2f7',
                  },
                  '&.Mui-selected': {
                    color: config.color,
                    bgcolor: config.bg,
                    border: `1px solid ${config.color}33`,
                    boxShadow: `0 4px 12px ${config.color}22`,
                  },
                }}
              />
            ))}
          </Tabs>
        </Paper>

        <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Chip
            label={`Active: ${leadTabConfig[leadTab].label}`}
            sx={{
              fontWeight: 700,
              color: leadTabConfig[leadTab].color,
              bgcolor: leadTabConfig[leadTab].bg,
              border: `1px solid ${leadTabConfig[leadTab].color}33`,
            }}
            size="small"
          />
          <Chip
            label={`Server Total: ${meta.count || 0}`}
            size="small"
            variant="outlined"
            sx={{ fontWeight: 600 }}
          />
        </Box>
        
        {/* Controls - Responsive Stack */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' }, 
          gap: { xs: 1, sm: 1.5, md: 2 },
          mb: 2,
          flexWrap: 'wrap'
        }}>
          <Button
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={handleOpenAddLead}
            sx={{
              bgcolor: '#4B5AB6',
              '&:hover': { bgcolor: '#3948a0' },
              textTransform: 'none',
              fontWeight: 700,
              flex: { xs: 1, sm: 'auto', md: '0 0 auto' },
              fontSize: { xs: '0.85rem', sm: '0.95rem', md: '1rem' },
              py: { xs: 1, sm: 1.25, md: 1.5 },
              px: { xs: 1.5, sm: 2, md: 2.5 },
              minWidth: { xs: 'auto', sm: 110, md: 'auto' },
              maxWidth: { xs: '100%', sm: 'auto', md: '160px' }
            }}
          >
            Add Lead
          </Button>
          <Button
            variant="outlined"
            startIcon={<UploadFileOutlinedIcon />}
            onClick={handleOpenBulkImport}
            sx={{
              borderColor: '#4B5AB6',
              color: '#4B5AB6',
              '&:hover': { borderColor: '#3948a0', bgcolor: '#eef2ff' },
              textTransform: 'none',
              fontWeight: 700,
              flex: { xs: 1, sm: 'auto', md: '0 0 auto' },
              fontSize: { xs: '0.85rem', sm: '0.95rem', md: '1rem' },
              py: { xs: 1, sm: 1.25, md: 1.5 },
              px: { xs: 1.5, sm: 2, md: 2.5 },
              minWidth: { xs: 'auto', sm: 110, md: 'auto' },
              maxWidth: { xs: '100%', sm: 'auto', md: '180px' }
            }}
          >
            Bulk Import
          </Button>
          <FormControl size="small" sx={{
            minWidth: { xs: 'auto', sm: 140, md: 160 },
            maxWidth: { xs: '100%', sm: 'auto', md: '200px' },
            flex: { xs: 1, sm: 'auto', md: '0 0 auto' }
          }}>
            <InputLabel sx={{ fontSize: { xs: '0.85rem', sm: '1rem' } }}>Month</InputLabel>
            <Select
              value={month}
              label="Month"
              onChange={e => setMonth(e.target.value)}
              sx={{ fontSize: { xs: '0.85rem', sm: '1rem' } }}
            >
              {monthOptions.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {/* Date Range Filters */}
          <TextField
            size="small"
            type="date"
            label="From Date"
            value={fromDate}
            onChange={e => setFromDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ 
              minWidth: { xs: 'auto', sm: 140, md: 160 },
              maxWidth: { xs: '100%', sm: 'auto', md: '180px' },
              flex: { xs: 1, sm: 'auto', md: '0 0 auto' }
            }}
          />
          <TextField
            size="small"
            type="date"
            label="To Date"
            value={toDate}
            onChange={e => setToDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ 
              minWidth: { xs: 'auto', sm: 140, md: 160 },
              maxWidth: { xs: '100%', sm: 'auto', md: '180px' },
              flex: { xs: 1, sm: 'auto', md: '0 0 auto' }
            }}
          />
          {/* Refresh Button */}
          <IconButton
            onClick={fetchLeads}
            disabled={loading}
            sx={{
              bgcolor: '#f8fafc',
              border: '1px solid #e2e8f0',
              '&:hover': { bgcolor: '#e0e7ff' },
              alignSelf: { xs: 'flex-start', sm: 'auto' }
            }}
            title="Refresh"
            size="small"
          >
            <RefreshIcon sx={{ color: '#4B5AB6', fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
          </IconButton>
        </Box>
        <Typography variant="body2" sx={{ color: '#64748b', mb: 2, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
          {meta.uiMsg}
        </Typography>

        {/* Filter Panel */}
        <Box
          sx={{
            p: { xs: 1, sm: 1.5 },
            mb: 2,
            border: '1px solid #e2e8f0',
            borderRadius: 2,
            bgcolor: '#f8fafc'
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr 1fr auto' },
              gap: 1,
              alignItems: 'center'
            }}
          >
            <TextField
              size="small"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search name, email, phone, plan, source..."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#64748b' }} fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

            <FormControl size="small">
              <InputLabel>Plan</InputLabel>
              <Select value={planFilter} label="Plan" onChange={e => setPlanFilter(e.target.value)}>
                <MenuItem value="all">All Plans</MenuItem>
                {planOptions.map(option => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small">
              <InputLabel>Source</InputLabel>
              <Select value={sourceFilter} label="Source" onChange={e => setSourceFilter(e.target.value)}>
                <MenuItem value="all">All Sources</MenuItem>
                {sourceOptions.map(option => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small">
              <InputLabel>Request Type</InputLabel>
              <Select value={requestTypeFilter} label="Request Type" onChange={e => setRequestTypeFilter(e.target.value)}>
                <MenuItem value="all">All Types</MenuItem>
                {requestTypeOptions.map(option => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              startIcon={<ClearAllIcon />}
              onClick={handleResetFilters}
              disabled={activeFilterCount === 0}
              sx={{ textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
            >
              Reset
            </Button>
          </Box>

          <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Chip label={`Filtered ${filteredLeads.length} / ${leads.length}`} size="small" sx={{ fontWeight: 700, bgcolor: '#e0e7ff', color: '#3730a3' }} />
            <Chip label={`Server Total ${meta.count || 0}`} size="small" variant="outlined" sx={{ fontWeight: 600 }} />
            <Chip label={`Page ${Math.min(page + 1, Math.max(1, Math.ceil(sortedLeads.length / rowsPerPage)))}`} size="small" variant="outlined" sx={{ fontWeight: 600 }} />
            {fromDate && (
              <Chip 
                label={`From: ${dayjs(fromDate).format('MMM DD, YYYY')}`} 
                size="small" 
                color="secondary" 
                onDelete={() => setFromDate('')}
                sx={{ fontWeight: 600 }}
              />
            )}
            {toDate && (
              <Chip 
                label={`To: ${dayjs(toDate).format('MMM DD, YYYY')}`} 
                size="small" 
                color="secondary" 
                onDelete={() => setToDate('')}
                sx={{ fontWeight: 600 }}
              />
            )}
            {activeFilterCount > 0 && (
              <Chip label={`${activeFilterCount} active filter${activeFilterCount > 1 ? 's' : ''}`} size="small" color="primary" />
            )}
          </Box>
        </Box>
        
        {/* Responsive Table Wrapper */}
        <Box sx={{ width: '100%', overflowX: 'auto', mb: 2 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{error}</Alert>
          ) : (
            <Table sx={{ minWidth: { xs: 650, sm: 900 } }}>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                  <TableCell sx={{ fontWeight: 700, fontSize: { xs: '0.7rem', sm: '0.875rem' }, p: { xs: 0.5, sm: 1 } }}>
                    <TableSortLabel
                      active={sortBy === 'fullName'}
                      direction={sortBy === 'fullName' ? sortOrder : 'asc'}
                      onClick={() => handleSort('fullName')}
                    >
                      Lead Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: { xs: '0.7rem', sm: '0.875rem' }, p: { xs: 0.5, sm: 1 }, display: { xs: 'none', sm: 'table-cell' } }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: { xs: '0.7rem', sm: '0.875rem' }, p: { xs: 0.5, sm: 1 }, display: { xs: 'none', lg: 'table-cell' } }}>Phone</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: { xs: '0.7rem', sm: '0.875rem' }, p: { xs: 0.5, sm: 1 }, display: { xs: 'none', md: 'table-cell' } }}>Country</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: { xs: '0.7rem', sm: '0.875rem' }, p: { xs: 0.5, sm: 1 }, display: { xs: 'none', lg: 'table-cell' } }}>Website</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: { xs: '0.7rem', sm: '0.875rem' }, p: { xs: 0.5, sm: 1 } }}>Plan</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: { xs: '0.7rem', sm: '0.875rem' }, p: { xs: 0.5, sm: 1 }, display: { xs: 'none', md: 'table-cell' } }}>Source</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: { xs: '0.7rem', sm: '0.875rem' }, p: { xs: 0.5, sm: 1 }, display: { xs: 'none', lg: 'table-cell' } }}>Request Type</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: { xs: '0.7rem', sm: '0.875rem' }, p: { xs: 0.5, sm: 1 }, display: { xs: 'none', lg: 'table-cell' } }}>Reason</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: { xs: '0.7rem', sm: '0.875rem' }, p: { xs: 0.5, sm: 1 }, display: { xs: 'none', md: 'table-cell' } }}>Employees</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: { xs: '0.7rem', sm: '0.875rem' }, p: { xs: 0.5, sm: 1 }, display: { xs: 'none', lg: 'table-cell' } }}>Best Time</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: { xs: '0.7rem', sm: '0.875rem' }, p: { xs: 0.5, sm: 1 } }}>
                    <TableSortLabel
                      active={sortBy === 'createdDate'}
                      direction={sortBy === 'createdDate' ? sortOrder : 'desc'}
                      onClick={() => handleSort('createdDate')}
                    >
                      Created On
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredLeads.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={12} align="center" sx={{ py: 5, color: '#64748b', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {leads.length === 0 ? 'No leads found for this month.' : 'No leads matched the current filters.'}
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedLeads.map((lead, index) => (
                    <TableRow
                      key={lead.leadId || `${lead.email || 'lead'}-${lead.createdDate || page * rowsPerPage + index}`}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => setSelectedLead(lead)}
                    >
                      <TableCell sx={{ fontWeight: 600, fontSize: { xs: '0.7rem', sm: '0.875rem' }, p: { xs: 0.5, sm: 1 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <span>{lead.fullName}</span>
                          <Tooltip title="View / add follow-up comments">
                            <Badge
                              badgeContent={(lead.comments || []).length}
                              showZero
                              overlap="circular"
                              sx={{
                                '& .MuiBadge-badge': {
                                  fontSize: '0.6rem',
                                  minWidth: 16,
                                  height: 16,
                                  fontWeight: 700,
                                  color: '#fff',
                                  bgcolor: (lead.comments || []).length ? '#DC2626' : '#94a3b8',
                                },
                              }}
                            >
                              <IconButton
                                size="small"
                                onClick={event => {
                                  event.stopPropagation();
                                  setCommentsLeadId(lead.leadId);
                                }}
                                sx={{
                                  bgcolor: '#EEF2FF',
                                  color: '#4B5AB6',
                                  width: 30,
                                  height: 30,
                                  transition: 'all 0.15s ease',
                                  '&:hover': { bgcolor: '#4B5AB6', color: '#fff', boxShadow: '0 4px 10px rgba(75, 90, 182, 0.35)' },
                                }}
                              >
                                <ChatBubbleOutlineIcon sx={{ fontSize: '1.05rem' }} />
                              </IconButton>
                            </Badge>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' }, p: { xs: 0.5, sm: 1 }, display: { xs: 'none', sm: 'table-cell' } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <span>{lead.email || '-'}</span>
                          <Tooltip title="Copy email">
                            <span>
                              <IconButton
                                size="small"
                                onClick={event => handleCopy(event, lead.email, 'Email')}
                                disabled={!lead.email}
                                sx={{ p: 0.4 }}
                              >
                                <ContentCopyIcon sx={{ fontSize: '0.95rem' }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' }, p: { xs: 0.5, sm: 1 }, display: { xs: 'none', lg: 'table-cell' } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <span>{lead.phoneNumber || '-'}</span>
                          <Tooltip title="Copy phone number">
                            <span>
                              <IconButton
                                size="small"
                                onClick={event => handleCopy(event, lead.phoneNumber, 'Phone number')}
                                disabled={!lead.phoneNumber}
                                sx={{ p: 0.4 }}
                              >
                                <ContentCopyIcon sx={{ fontSize: '0.95rem' }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' }, p: { xs: 0.5, sm: 1 }, display: { xs: 'none', md: 'table-cell' } }}>{lead.country ? lead.country.toUpperCase() : '-'}</TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' }, p: { xs: 0.5, sm: 1 }, display: { xs: 'none', lg: 'table-cell' } }}>
                        {lead.companyWebsite ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <a href={lead.companyWebsite} target="_blank" rel="noopener noreferrer" style={{ color: '#4B5AB6', textDecoration: 'none' }} onClick={(e) => e.stopPropagation()}>
                              {lead.companyWebsite.replace(/^https?:\/\/(www\.)?/, '').substring(0, 25)}
                            </a>
                            <Tooltip title="Copy website URL">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={event => handleCopy(event, lead.companyWebsite, 'Website URL')}
                                  sx={{ p: 0.4 }}
                                >
                                  <ContentCopyIcon sx={{ fontSize: '0.95rem' }} />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        ) : '-'}
                      </TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' }, p: { xs: 0.5, sm: 1 } }}>{lead.planType}</TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' }, p: { xs: 0.5, sm: 1 }, display: { xs: 'none', md: 'table-cell' } }}>{lead.source}</TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' }, p: { xs: 0.5, sm: 1 }, display: { xs: 'none', lg: 'table-cell' } }}>{lead.requestType}</TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' }, p: { xs: 0.5, sm: 1 }, display: { xs: 'none', lg: 'table-cell' } }}>{lead.reasonForRequest}</TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' }, p: { xs: 0.5, sm: 1 }, display: { xs: 'none', md: 'table-cell' } }}>{lead.numberOfEmployees}</TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' }, p: { xs: 0.5, sm: 1 }, display: { xs: 'none', lg: 'table-cell' } }}>{lead.bestTimeToConnect}</TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' }, p: { xs: 0.5, sm: 1 } }}>
                        {lead.createdDate ? new Date(lead.createdDate).toLocaleString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit'
                        }) : '-'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </Box>
        {!loading && !error && filteredLeads.length > 0 && (
          <TablePagination
            component="div"
            count={sortedLeads.length}
            page={page}
            onPageChange={(_, nextPage) => setPage(nextPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={event => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50]}
          />
        )}
        <Box sx={{ mt: 2, color: '#4B5AB6', fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
          Visible Leads: {filteredLeads.length} | This Page: {paginatedLeads.length} | Total {leadTabConfig[leadTab].label}: {meta.count}
        </Box>
      </Paper>
      {/* ADD: Lead Details Modal */}
      <LeadDetailsModal
        open={!!selectedLead}
        lead={selectedLead}
        onClose={() => setSelectedLead(null)}
        onUpdated={fetchLeads}
      />
      <AddLeadDialog
        open={openAddLead}
        onClose={handleCloseAddLead}
        onSuccess={handleLeadCreated}
      />
      <BulkImportLeadsDialog
        open={openBulkImport}
        onClose={handleCloseBulkImport}
        onSuccess={fetchLeads}
      />
      <FollowUpCommentsDialog
        open={!!commentsLeadId}
        lead={leads.find(l => l.leadId === commentsLeadId) || null}
        onClose={() => setCommentsLeadId(null)}
        onCommentAdded={fetchLeads}
      />
      <Snackbar
        open={Boolean(copyMessage)}
        autoHideDuration={1800}
        onClose={() => setCopyMessage('')}
        message={copyMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default Leads;